import { useStateXAtom } from '@cloudio-saas/statex';
import { Box, Grid, Theme, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Platform } from 'componentregistry';
import { DialogPopup } from 'components/Popup/DialogPopup';
import DataSourceProperties from 'expression/builder/DataSourceProperties';
import ItemProperties, {
  ItemIdPropertyPicker,
} from 'expression/builder/ItemProperties';
import {
  categorySelectedAtom,
  expressionPropsAtom,
  selectedFunctionAtom,
} from 'expression/builder/state';
import useComponentSize from 'hooks/useComponentSize';
import useConstant from 'hooks/useConstant';
import { TreeView } from 'layout/Sidebar/panels/TreeView/TreeViewPanel';
import { usePlatform } from 'platform/PlatformHooks';
import React, { Suspense, useMemo, useRef } from 'react';
import SimpleLoading from 'root/SimpleLoading';
import assert from 'utils/assert';
import { PubSub } from 'utils/PubSub';
import Entries from './Entries';
import ExpressionActions from './ExpressionActions';
import ExpressionInput, { EventMap } from './ExpressionInput';
import {
  categoriesArr,
  getCategoryLabel,
  getFunction,
  getFunctionLabel,
  getFunctionsByCategory,
} from './Functions';
import { FunctionOrPropertyTip } from './Tip';

const INPUT_HEIGHT = 88;

const useStyles = makeStyles((theme: Theme) => ({
  input: {
    border: 0,
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: 0,
    '& textarea': {
      backgroundColor: theme.palette.background.paper,
      resize: 'none',
      height: INPUT_HEIGHT - 1,
      padding: '8px 8px',
      boxSizing: 'border-box',
    },
  },
  body: {
    backgroundColor: theme.palette.background.paper,
  },
  actions: {
    backgroundColor: theme.palette.background.default,
    padding: 12,
  },
  left: {
    flexGrow: 0,
  },
  middle: {
    flexGrow: 0,
    borderLeft: `1px solid ${theme.palette.divider}`,
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  right: {
    flexGrow: 1,
  },
  tip: {
    backgroundColor: theme.palette.background.paper,
  },
  textAreaWrapper: {},
  treeHeader: {
    backgroundColor: theme.palette.background.default,
  },
  result: {
    paddingLeft: 16,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flex: 1,
  },
  error: {
    color: theme.palette.error.main,
  },
  mr: {
    marginRight: 8,
  },
}));

const HEIGHT = 600;
const WIDTH = 960;
const CATEGORY_WIDTH = 150;

export function ExpressionBuilder({ platform }: { platform: Platform }) {
  const popupRef = useRef<HTMLDivElement | null>(null);
  const [category, setCategory] = useStateXAtom(categorySelectedAtom);
  const [func, setFunc] = useStateXAtom(selectedFunctionAtom);
  const pubsub = useConstant<PubSub<EventMap>>(() => new PubSub<EventMap>());
  const [expressionProps, setExpressionProps] =
    useStateXAtom(expressionPropsAtom);

  const {
    open,
    value: expression,
    context: expressionContext,
  } = expressionProps;
  const { appUid, pageId, itemId } = expressionContext;
  if (!appUid) {
    return null;
  }
  assert(
    typeof expression.value === 'string',
    'expression.value must be a string',
  );

  const onCategoryChange = (currentCategory: string) => {
    setCategory(currentCategory);
    const functions = getFunctionsByCategory(currentCategory);
    if (functions?.length) {
      setFunc(functions[0]);
    } else {
      setFunc('');
    }
    // ta?.focus();
  };

  const onFunctionChange = (currentFunction: string) => {
    setFunc(currentFunction);
    const { usage } = getFunction(category, currentFunction);
    pubsub.pub('insertAt', usage);
  };

  const onDataSourceChange = (dsAlias: string) => {
    setFunc(dsAlias);
  };

  const onMouseEnter = (currentFunction: string) => {
    setFunc(currentFunction);
  };

  const handleClose = () => {
    setExpressionProps((props) => ({
      ...props,
      open: false,
    }));
    // setInit(false);
  };

  const onPropertySelect = (text: string) => {
    pubsub.pub('insertAt', text);
  };

  return (
    <DialogPopup
      platform={platform}
      appUid={appUid}
      popupref={popupRef}
      popupHeight={HEIGHT}
      open={open}
      popupWidth={WIDTH}
      modal={false}
      onClose={handleClose}
      bodyPadding={false}
      pageId="$sidebar$"
      itemId={`expbldr-${itemId}`}
      bodyChildren={
        <ExpressionBody
          expression={expression.value}
          height={-1}
          width={WIDTH}
          category={category}
          funOrProperty={func}
          onCategoryChange={onCategoryChange}
          onFunctionChange={onFunctionChange}
          onDataSourceChange={onDataSourceChange}
          onPropertySelect={onPropertySelect}
          onMouseEnter={onMouseEnter}
          appUid={appUid}
          pageUid={pageId}
          pubsub={pubsub}
        />
      }
      Header={
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          Expression Builder
          <Typography sx={{ marginLeft: 2 }} variant="caption">
            Any valid ES2020 Javascript
          </Typography>
        </Box>
      }
      popupActions={<ExpressionActions pubsub={pubsub} />}
      styles={{ padding: 0 }}
    />
  );
}

const ExpressionBody = ({
  expression,
  height,
  width,
  category,
  funOrProperty,
  onCategoryChange,
  onFunctionChange,
  onDataSourceChange,
  onPropertySelect,
  onMouseEnter,
  appUid,
  pageUid,
  pubsub,
}: {
  expression: string;
  height: number;
  width: number;
  category: string;
  funOrProperty: string;
  onCategoryChange: (newCateg: string) => void;
  onFunctionChange: (func: string) => void;
  onDataSourceChange: (func: string) => void;
  onPropertySelect: (propPath: string) => void;
  onMouseEnter: (func: string) => void;
  appUid: string;
  pageUid: string;
  pubsub: PubSub<EventMap>;
}) => {
  const platform = usePlatform();
  const iipp = useComponentSize();

  // const [localValue, setLocalValue] = useState(expression);
  const getDsEntries = useMemo(() => {
    const dsKeys: string[] = [];
    platform.getDataSources(appUid, pageUid)?.forEach((value, key) => {
      if (!platform?.getStore(appUid, pageUid, key)?.programmatic) {
        dsKeys.push(key);
      }
    });
    return dsKeys;
  }, [appUid, pageUid, platform]);
  const classes = useStyles();
  let categoryTitle: string;
  if (category === 'operator') {
    categoryTitle = 'Choose an Operator';
  } else if (category === 'params') {
    categoryTitle = 'Choose a Paramater';
  } else if (['session', 'screen'].includes(category)) {
    categoryTitle = 'Choose a Property';
  } else if (category === 'dataSource') {
    categoryTitle = 'Choose a Data Source';
  } else {
    categoryTitle = 'Choose a Function';
  }
  const h = height - INPUT_HEIGHT - iipp.height;

  const getDisplayForCategory = () => {
    switch (category) {
      case 'itemProperty':
        return (
          <>
            <Grid
              item
              xs
              className={classes.middle}
              style={{
                flexBasis: CATEGORY_WIDTH * 3,
                position: 'relative',
              }}
            >
              <TreeView
                treename="expressionTree"
                noHeader={false}
                headerClassName={classes.treeHeader}
                height={h}
                width={CATEGORY_WIDTH * 3}
                platform={platform}
                appUid={appUid}
                pageUid={pageUid}
              />
            </Grid>
            <Grid item xs className={classes.right}>
              <ItemProperties
                appUid={appUid}
                treename="expressionTree"
                onSelect={onPropertySelect}
                height={h}
                width={width - CATEGORY_WIDTH * 4 - 16}
                pageUid={pageUid}
              />
            </Grid>
          </>
        );
      case 'dataSource':
        return (
          <>
            <Grid
              item
              xs
              className={classes.middle}
              style={{
                flexBasis: CATEGORY_WIDTH * 3,
              }}
            >
              <Entries
                entries={getDsEntries}
                onClick={onDataSourceChange}
                // onMouseEnter={onMouseEnter}
                height={h}
                title={categoryTitle}
                width={CATEGORY_WIDTH * 3}
                value={funOrProperty}
                getLabel={(value) => value}
              />
            </Grid>
            <Grid item xs className={classes.right}>
              <Suspense fallback={<SimpleLoading name="exp" />}>
                {funOrProperty ? (
                  <DataSourceProperties
                    dsAlias={funOrProperty}
                    onSelect={onPropertySelect}
                    height={h}
                    width={width - CATEGORY_WIDTH * 4 - 16}
                    pageUid={pageUid}
                    appUid={appUid}
                  />
                ) : null}
              </Suspense>
            </Grid>
          </>
        );
      default:
        return (
          <>
            <Grid
              item
              xs
              className={classes.middle}
              style={{
                flexBasis: CATEGORY_WIDTH,
              }}
            >
              <Entries
                entries={getFunctionsByCategory(category)}
                onClick={onFunctionChange}
                onMouseEnter={onMouseEnter}
                height={h}
                title={categoryTitle}
                width={CATEGORY_WIDTH}
                value={funOrProperty}
                getLabel={(value) => getFunctionLabel(category, value)}
              />
            </Grid>
            <Grid item xs className={classes.tip}>
              <FunctionOrPropertyTip
                category={category}
                funOrProperty={funOrProperty}
              />
            </Grid>
          </>
        );
    }
  };

  return (
    <div className={classes.body}>
      <Grid container item xs>
        <Grid
          item
          xs
          className={classes.left}
          style={{ flexBasis: CATEGORY_WIDTH }}
        >
          <Entries
            entries={categoriesArr}
            onClick={onCategoryChange}
            height={h}
            title="Categories"
            width={CATEGORY_WIDTH}
            value={category}
            getLabel={(value) => getCategoryLabel(value)}
          />
        </Grid>
        {getDisplayForCategory()}
      </Grid>
      {category === 'itemProperty' ? (
        <ItemIdPropertyPicker
          appUid={appUid}
          pageUid={pageUid}
          ref={iipp.ref}
          treename="expressionTree"
        />
      ) : null}
      <ExpressionInput value={expression} pubsub={pubsub} />
    </div>
  );
};
